"use client";

import React, { useState } from "react";
import { ArrowRight, Code as Code2, Compass, Cpu, GraduationCap, Layers, MapPin, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const DEFAULT_MASCOT = {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/af0573d1-cac5-46e2-816e-4e00b0b25dc3-1.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/af0573d1-cac5-46e2-816e-4e00b0b25dc3-1.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/af0573d1-cac5-46e2-816e-4e00b0b25dc3-1.png-wvc-sizes",
  alt: "ROBOTO Academy Mascot"
};
function StudentProjectsConsumer({
  lang
}: {
  lang: "en" | "vi";
}) {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="flex flex-col gap-3">
        {[0, 1, 2].map(i => <div key={i} data-index={i} className="flex gap-3 items-center rounded-lg border border-border p-3 bg-muted/30">
            <Skeleton className="h-14 w-14 rounded-md shrink-0" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>)}
      </div>;
  }
  if (error || !posts || posts.length === 0) {
    return <div className="rounded-lg border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
        {lang === "vi" ? "Chưa có dự án nào được đăng tải." : "No student projects published yet."}
      </div>;
  }
  return <div className="flex flex-col gap-3">
      {posts.map((post, i) => {
      const custom = post.customFields || {};
      const title = custom.project_name || post.title || (lang === "vi" ? "Dự án học viên" : "Student Project");
      const domain = custom.field_domain || "STEM / Robotics";
      const desc = custom.short_description || "";
      const imageObj = {
        src: custom.image || post.featuredImage || "",
        srcset: "",
        sizes: ""
      };
      return <Link key={post.id} data-index={i} to={post.link || "#"} className="group block rounded-lg border border-border bg-background p-3 transition-[border-color,background-color] hover:border-primary/50 hover:bg-muted/40">
            <div className="flex items-start gap-3 min-w-0">
              {imageObj.src ? <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-md border border-border bg-muted">
                  <img src={imageObj.src} data-wvc-srcset={imageObj.srcset} data-wvc-sizes={imageObj.sizes} alt={title} loading="lazy" className="h-full w-full object-cover" />
                </div> : <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md border border-border bg-muted text-muted-foreground">
                  <Cpu className="size-6 text-primary" />
                </div>}
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-1 min-w-0">
                  <Badge variant="outline" className="text-[10px] uppercase font-mono tracking-wider py-0 px-1.5 text-primary border-primary/30 shrink-0">
                    {domain}
                  </Badge>
                  <ArrowRight className="size-3.5 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary shrink-0" />
                </div>
                <h4 className="mt-1 line-clamp-1 block min-w-0 text-sm font-semibold text-foreground group-hover:text-primary">
                  {title}
                </h4>
                {desc ? <p className="line-clamp-1 block min-w-0 text-xs text-muted-foreground">
                    {desc}
                  </p> : null}
              </div>
            </div>
          </Link>;
    })}
    </div>;
}
function CenterNetworkConsumer({
  lang
}: {
  lang: "en" | "vi";
}) {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="space-y-4">
        <Skeleton className="h-10 w-28" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-16 w-full rounded-md" />
      </div>;
  }
  const count = posts ? posts.length : 0;
  const sampleCenter = posts && posts[0]?.customFields;
  return <div className="space-y-4">
      <div>
        <div className="flex items-baseline gap-2">
          <span className="font-mono text-4xl font-bold tracking-tight text-primary">
            {error ? "—" : count}
          </span>
          <span className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
            {lang === "vi" ? "Cơ sở hoạt động" : "Operational Centers"}
          </span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {lang === "vi" ? "Mạng lưới cơ sở vật chất chuẩn quốc tế với phòng lab chuyên dụng." : "Physical training spaces equipped with modular robotics test benches."}
        </p>
      </div>

      {sampleCenter && <div className="rounded-lg border border-border bg-muted/30 p-3 text-xs min-w-0">
          <div className="flex items-center gap-1.5 font-medium text-foreground min-w-0">
            <MapPin className="size-3.5 shrink-0 text-primary" />
            <span className="line-clamp-1 block flex-1 min-w-0">{sampleCenter.center_name || posts[0].title}</span>
          </div>
          {sampleCenter.address ? <p className="mt-1 line-clamp-2 block min-w-0 text-muted-foreground pl-5">
              {sampleCenter.address}
            </p> : null}
        </div>}
    </div>;
}
export default function Pillars() {
  const [lang, setLang] = useState<"en" | "vi">("vi");
  const projectParams = {
    post_type: "project",
    per_page: 3,
    status: ["publish"]
  };
  const projectQuery = new WP_Query(projectParams);
  const centerParams = {
    post_type: "center",
    per_page: 20,
    status: ["publish"]
  };
  const centerQuery = new WP_Query(centerParams);
  return <section id="corepillars" data-nav="light" className="relative bg-background py-16 md:py-24 text-foreground">
      {/* Decorative Floating Mascot */}
      <div className="pointer-events-none select-none absolute top-4 right-4 sm:top-6 sm:right-6 lg:top-8 lg:right-8 z-0">
        <img src={DEFAULT_MASCOT.src} data-wvc-srcset={DEFAULT_MASCOT.srcset} data-wvc-sizes={DEFAULT_MASCOT.sizes} alt={DEFAULT_MASCOT.alt} loading="lazy" className="h-16 w-16 sm:h-20 sm:w-20 md:h-24 md:w-24 lg:h-28 lg:w-28 bg-transparent object-contain drop-shadow-md pointer-events-none select-none" />
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        {/* Header row with bilingual toggle */}
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between border-b border-border pb-8">
          <div className="max-w-2xl pr-16 sm:pr-24 md:pr-0">
            <div className="flex items-center gap-2">
              <span className="inline-block size-2 rounded-full bg-primary" />
              <span className="font-mono text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {lang === "vi" ? "Nền tảng đào tạo chuẩn mực" : "Operational Standards"}
              </span>
            </div>
            <h2 className="mt-2 text-2xl font-bold tracking-tight text-foreground md:text-3xl lg:text-4xl">
              {lang === "vi" ? "Ba trụ cột học thuật tại ROBOTO" : "Three core pillars of our engineering academy"}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground md:text-base">
              {lang === "vi" ? "Không dùng các cam kết mơ hồ hay giải thưởng tự phong. Chúng tôi công khai lộ trình học chuẩn, các dự án học viên thực tế và mạng lưới trung tâm vật lý." : "Explore verified tracks, hands-on student builds, and our lab network across Vietnam without inflated metrics."}
            </p>
          </div>

          {/* Bilingual Toggle Button */}
          <div className="flex items-center gap-1 rounded-md border border-border bg-muted/40 p-1 self-start md:self-auto shrink-0 md:mr-24 lg:mr-28">
            <Button type="button" variant={lang === "vi" ? "default" : "ghost"} size="sm" className="h-7 px-3 text-xs" onClick={() => setLang("vi")}>
              Tiếng Việt
            </Button>
            <Button type="button" variant={lang === "en" ? "default" : "ghost"} size="sm" className="h-7 px-3 text-xs" onClick={() => setLang("en")}>
              English
            </Button>
          </div>
        </div>

        {/* Modular 3-Column Pillar Architecture */}
        <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Pillar 1: Curriculum Clarity */}
          <Card className="flex flex-col justify-between border-border bg-card shadow-xs transition-[border-color,box-shadow] hover:border-primary/40 hover:shadow-md">
            <div>
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Compass className="size-5" />
                  </div>
                  <Badge variant="outline" className="font-mono text-xs">
                    4–16 {lang === "vi" ? "Tuổi" : "Ages"}
                  </Badge>
                </div>
                <CardTitle className="mt-4 text-lg font-semibold text-card-foreground">
                  {lang === "vi" ? "Lộ trình học tập minh bạch" : "Curriculum Clarity"}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-muted-foreground">
                <p>
                  {lang === "vi" ? "Chương trình liên thông qua 4 cấp độ kỹ thuật: Khám phá mầm non, Cơ điện tử căn bản, Lập trình điều khiển và Sáng chế AI ứng dụng." : "Progressive four-stage continuum covering early STEM exploration, mechanics, embedded algorithms, and real-world applied robotics."}
                </p>
                <div className="space-y-2 pt-2 border-t border-border/60">
                  <div className="flex items-center gap-2 text-xs text-foreground">
                    <GraduationCap className="size-4 text-primary" />
                    <span>
                      {lang === "vi" ? "Đánh giá theo cột mốc sản phẩm thực tế" : "Milestone-verified project completions"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-foreground">
                    <Layers className="size-4 text-primary" />
                    <span>
                      {lang === "vi" ? "Chuẩn LEGO Education & VEX Robotics" : "Aligned with international STEM standards"}
                    </span>
                  </div>
                </div>
              </CardContent>
            </div>
            <div className="p-6 pt-0">
              <Button asChild variant="outline" className="w-full justify-between">
                <Link to="/LearningPathCurriculumPage">
                  <span>{lang === "vi" ? "Xem toàn bộ khung lộ trình" : "View Curriculum Tracks"}</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </Card>

          {/* Pillar 2: Real Student Projects */}
          <Card className="flex flex-col justify-between border-border bg-card shadow-xs transition-[border-color,box-shadow] hover:border-primary/40 hover:shadow-md">
            <div>
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Code2 className="size-5" />
                  </div>
                  <span className="font-mono text-xs text-muted-foreground">
                    {lang === "vi" ? "Dự án mới nhất" : "Live Builds"}
                  </span>
                </div>
                <CardTitle className="mt-4 text-lg font-semibold text-card-foreground">
                  {lang === "vi" ? "Dự án kỹ thuật thực tế" : "Student Engineering Projects"}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  {lang === "vi" ? "Dự án chế tạo nguyên mẫu bởi học viên ROBOTO qua các học kỳ nghiên cứu:" : "Real prototypes authored and coded by students during laboratory cycles:"}
                </p>
                <PostsProvider wp_query={{
                post_type: "project",
                per_page: 3,
                status: ["publish"]
              }}>
                  <StudentProjectsConsumer lang={lang} />
                </PostsProvider>
              </CardContent>
            </div>
            <div className="p-6 pt-0">
              <Button asChild variant="outline" className="w-full justify-between">
                <Link to="/ProjectsCompetitionsPage">
                  <span>{lang === "vi" ? "Khám phá phòng dự án" : "All Student Projects"}</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </Card>

          {/* Pillar 3: Physical Center Network */}
          <Card className="flex flex-col justify-between border-border bg-card shadow-xs transition-[border-color,box-shadow] hover:border-primary/40 hover:shadow-md">
            <div>
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <MapPin className="size-5" />
                  </div>
                  <Badge variant="secondary" className="font-mono text-xs">
                    {lang === "vi" ? "Xác thực cơ sở" : "Verified Labs"}
                  </Badge>
                </div>
                <CardTitle className="mt-4 text-lg font-semibold text-card-foreground">
                  {lang === "vi" ? "Mạng lưới trung tâm đào tạo" : "Physical Center Network"}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <PostsProvider wp_query={{
                post_type: "center",
                per_page: 20,
                status: ["publish"]
              }}>
                  <CenterNetworkConsumer lang={lang} />
                </PostsProvider>
                <div className="rounded-lg bg-muted/40 p-3 text-xs text-muted-foreground border border-border/50">
                  <div className="flex items-center gap-1.5 font-medium text-foreground mb-1">
                    <Sparkles className="size-3.5 text-primary" />
                    <span>{lang === "vi" ? "Trang thiết bị chuẩn học viện" : "Academy Lab Criteria"}</span>
                  </div>
                  {lang === "vi" ? "Mỗi phòng lab trang bị trạm hàn an toàn, máy in 3D, bộ kit LEGO Mindstorms/SPIKE và sân thử đấu trường robot tiêu chuẩn." : "Each lab houses safety testing stations, 3D rapid prototyping, and regulation competition test tracks."}
                </div>
              </CardContent>
            </div>
            <div className="p-6 pt-0">
              <Button asChild variant="default" className="w-full justify-between">
                <Link to="/FacilitiesCentersPage">
                  <span>{lang === "vi" ? "Tìm trung tâm gần bạn" : "Locate a Facility"}</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </section>;
}